/**
 * English overlay for the Charger catalog.
 * The Charger DB entity stores Danish text only. This module maps the
 * language-agnostic charger records (keyed by SKU) to English for the /en pages.
 *
 * Usage:
 *   import { enCharger } from '@/components/en/enChargerOverlay';
 *   const c = enCharger(rawCharger);   // returns a shallow copy with English fields
 */

// Danish spec label -> English
const SPEC_LABELS = {
  'Opladningseffekt': 'Charging power',
  'Maks. strøm': 'Maximum current',
  'Nettype': 'Supply network',
  'Dimensioner (H × B × D)': 'Dimensions (H × W × D)',
  'RFID': 'RFID',
  'Kabellås': 'Cable lock',
  'Lastbalancering': 'Load balancing',
  'Automatisk 1-/3-faseskift': 'Automatic 1-/3-phase switching',
  'V2X / V2G': 'V2X / V2G',
  'Fejlstrømsbeskyttelse': 'Residual-current protection',
  '1-faset': 'Single-phase',
  'Spænding': 'Voltage',
  'Stiktype': 'Connector type',
  'Forbindelse': 'Connectivity',
  'Protokoller': 'Protocols',
  'Måler': 'Meter',
  'Skærm': 'Display',
  'V2G': 'V2G',
  'IP-klasse': 'IP rating',
  'IP/IK-klasse': 'IP/IK rating',
  'Garanti': 'Warranty',
  'Dimensioner': 'Dimensions',
  'Vægt': 'Weight',
  'Temperatur': 'Temperature',
  'Beskyttelse': 'Protection',
  'Fase-balancering': 'Phase balancing',
};

// Danish spec value -> English (only values that contain Danish words)
const SPEC_VALUES = {
  'Forberedt til V2G': 'V2G-ready hardware',
  'Forberedt til V2X': 'V2X-ready hardware',
  '230 / 400 V AC (±10 %)': '230 / 400 V AC (±10%)',
  'Type 2-stik (IEC 62196-2), uden fast kabel': 'Type 2 socket (IEC 62196-2), no tethered cable',
  'Wi-Fi 2,4 GHz + 4G LTE-M + Bluetooth BLE 4.2': 'Wi-Fi 2.4 GHz + 4G LTE-M + Bluetooth BLE 4.2',
  'Wi-Fi 2,4 GHz + 4G LTE Cat 1 + Bluetooth BLE 4.2': 'Wi-Fi 2.4 GHz + 4G LTE Cat 1 + Bluetooth BLE 4.2',
  'Wi-Fi 2,4 GHz + 4G eSIM + Ethernet RJ45 + Bluetooth BLE 4.2': 'Wi-Fi 2.4 GHz + 4G eSIM + Ethernet RJ45 + Bluetooth BLE 4.2',
  'OCPP 1.6J (cloud eller lokalt)': 'OCPP 1.6J (cloud or local)',
  'OCPP 1.6J (lokalt eller cloud)': 'OCPP 1.6J (local or cloud)',
  'Lokal OCPP 1.6-J / 2.0.1': 'Local OCPP 1.6-J / 2.0.1',
  'Indbygget 3-faset energimåler, ikke MID-certificeret': 'Integrated three-phase meter, not MID-certified',
  'MID Class B, bidirektionel energimåler': 'MID Class B, bidirectional energy meter',
  'Indbygget energimåler, ±1 % nøjagtighed': 'Integrated energy meter, ±1% accuracy',
  '1,3 kg': '1.3 kg',
  '1,6 kg': '1.6 kg',
  '2,3 kg (socket-variant)': '2.3 kg (socket variant)',
  '−30 °C til +40 °C': '−30 °C to +40 °C',
  '−30 °C til +50 °C': '−30 °C to +50 °C',
  'RGBW LED-statusring, intet display': 'RGBW LED status ring, no display',
  'Bagbelyst IPS LCD + RGBW LED-statusring': 'Backlit IPS LCD + RGBW LED status ring',
  'LED-statusindikator, intet LCD-display': 'LED status indicator, no LCD display',
  'Elektronisk, med permanent låsning': 'Electronic, with permanent locking',
  'Op til 3 bokse; dynamisk med Zaptec Sense (tilkøb)': 'Up to 3 chargers; dynamic with Zaptec Sense (optional)',
  'Op til 5 bokse; dynamisk med NexBlue Zen (tilkøb)': 'Up to 5 chargers; dynamic with NexBlue Zen (optional)',
  'Ikke angivet i producentens produktblad': 'Not specified in the manufacturer’s product sheet',
  'Understøttet; solopladning afhænger af udstyr og marked': 'Supported; solar charging depends on equipment and market',
  'Ja, automatisk 3-til-1-faseskift': 'Yes, automatic 3-to-1-phase switching',
  'Nej, understøtter ikke ISO 15118': 'No, ISO 15118 is not supported',
  'V2G-ready, ISO 15118-20 hardware; kræver kompatibelt setup': 'V2G-ready, ISO 15118-20 hardware; compatible setup required',
  'V2X-ready, ISO 15118; V2G og Plug & Charge afhænger af setup': 'V2X-ready, ISO 15118; V2G and Plug & Charge depend on setup',
  'Indbygget 6 mA DC (IEC 62955); ekstern beskyttelse efter installationskrav': 'Integrated 6 mA DC (IEC 62955); external protection per installation requirements',
  'Indbygget 6 mA DC (IEC 62955); ekstern RCD og automatsikring efter lokale krav': 'Integrated 6 mA DC (IEC 62955); external RCD and circuit breaker per local requirements',
  'Indbygget 6 mA DC (IEC 62955) + 30 mA AC (IEC 60947-2, Annex M)': 'Integrated 6 mA DC (IEC 62955) + 30 mA AC (IEC 60947-2, Annex M)',
  'Op til 22 kW': 'Up to 22 kW',
  'Op til 22 kW (32A)': 'Up to 22 kW (32A)',
  'Op til 7,4 kW (32A)': 'Up to 7.4 kW (32A)',
  'MID-certificeret': 'MID-certified',
  'Indbygget display': 'Built-in display',
  'Ready (bidirektionel)': 'Ready (bidirectional)',
  'Patenteret (1/3-faset auto)': 'Patented (1/3-phase auto)',
  '5 år': '5 years',
  '1,4–22 kW': '1.4–22 kW',
  '2,3 kg': '2.3 kg',
  '-30°C til +50°C': '-30°C to +50°C',
};

// Danish feature-pill text -> English (the small tags shown on charger cards)
const FEATURES = {
  'Op til 22 kW': 'Up to 22 kW',
  'Wi-Fi + 4G + Bluetooth': 'Wi-Fi + 4G + Bluetooth',
  'RFID og elektronisk kabellås': 'RFID and electronic cable lock',
  'V2G-ready hardware': 'V2G-ready hardware',
  'V2G-forberedt teknologi': 'V2G-ready hardware',
  'MID Class B og LCD-display': 'MID Class B and LCD display',
  'Automatisk 1-/3-faseskift': 'Automatic 1-/3-phase switching',
  'V2X-ready med ISO 15118': 'V2X-ready with ISO 15118',
  'V2X-forberedt med ISO 15118': 'V2X-ready with ISO 15118',
  'Wi-Fi + Ethernet + 4G eSIM': 'Wi-Fi + Ethernet + 4G eSIM',
  'Automatisk faseskift': 'Automatic phase switching',
  'V2X ready (fremtidssikret)': 'V2X ready (future-proof)',
  'V2G bidirektionel opladning': 'V2G bidirectional charging',
  'V2G bidirektionel': 'V2G bidirectional',
  'Solcelle-integration': 'Solar integration',
  'Skarp pris fra enkel': 'Sharp price from enkel',
  'Wi-Fi + 4G connectivity': 'Wi-Fi + 4G connectivity',
  'Wi-Fi + 4G': 'Wi-Fi + 4G',
  'Load balancing til flere biler': 'Load balancing for multiple cars',
  'Load balancing': 'Load balancing',
  'RFID adgangskontrol': 'RFID access control',
  'RFID': 'RFID',
  'Nyeste Zaptec model': 'Newest Zaptec model',
  'Fase-balancering': 'Phase balancing',
  'Patenteret fase-balancering': 'Patented phase balancing',
  'Patenteret fasebalancering': 'Patented phase balancing',
  'Dansk design': 'Danish design',
  'Norsk design': 'Norwegian design',
  '22 kW': '22 kW',
  '5 års garanti': '5-year warranty',
  'Indbygget måler (MID)': 'Built-in meter (MID)',
  'Indbygget måler': 'Built-in meter',
};

function translateFeatures(features) {
  if (!Array.isArray(features)) return features;
  return features.map((f) => FEATURES[f] || f);
}

// Per-SKU English overlay for free-text fields (name stays — it's a brand name)
const BY_SKU = {
  zaptec_go: {
    tagline: 'Compact design · Reliable home charging',
    description: 'A compact home charger with up to 22 kW charging, Wi-Fi, 4G LTE-M, Bluetooth and RFID access. Charging power depends on the car and installation. Dynamic load balancing requires optional Zaptec Sense.',
    meter_note_title: 'Integrated energy meter, not MID-certified',
    meter_note_body: 'The manufacturer specifies an integrated three-phase energy meter without MID certification. Meter accuracy is not the same as certification and does not guarantee tax-refund eligibility.',
    key_features: ['Up to 22 kW, subject to car and installation', 'Wi-Fi, 4G LTE-M and Bluetooth BLE 4.2', 'Local or cloud OCPP 1.6J', 'RFID access and electronic cable lock', 'Optional Zaptec Sense dynamic load balancing', 'Integrated meter, not MID-certified', 'IP54 / IK08 and UV-resistant enclosure', 'Five-year manufacturer warranty'],
    short_desc: 'Nordic quality and reliability',
    unique_point: 'Load balancing – reliable operation',
    badge: 'Classic',
  },
  zaptec_go_2: {
    tagline: 'MID metering and display · V2G-ready',
    short_desc: 'MID-certified metering and future-ready hardware',
    unique_point: 'Bidirectional MID meter and ISO 15118 hardware',
    description: 'Go 2 combines up to 22 kW charging, a bidirectional MID Class B meter, an IPS LCD and ISO 15118 hardware. V2G readiness is not an active export service: compatible cars, software, installation, additional grid protection, approvals and market services are required.',
    meter_note_title: 'MID-certified bidirectional energy meter',
    meter_note_body: 'MID Class B metering and a built-in display. V2G requires a compatible vehicle, software, installation, additional protection, approvals and a supporting energy service.',
    key_features: ['Up to 22 kW, subject to car and installation', 'Wi-Fi, 4G LTE Cat 1 and Bluetooth BLE 4.2', 'Local or cloud OCPP 1.6J', 'Bidirectional MID Class B meter', 'Backlit IPS LCD and LED status ring', 'ISO 15118-20 hardware, V2G-ready', 'Automatic 1-/3-phase switching; availability depends on setup and market', 'Optional Zaptec Sense dynamic load balancing', 'IP54 / IK10 and five-year manufacturer warranty'],
    badge: 'In stock soon',
  },
  nexblue_edge_2: {
    tagline: 'Ethernet and 4G · V2X-ready',
    short_desc: 'Flexible connectivity and V2X-ready hardware',
    unique_point: 'V2X-ready with ISO 15118 and local OCPP 2.0.1',
    description: 'NexBlue Edge 2 offers 1.4–22 kW charging with Wi-Fi, Ethernet, 4G eSIM and Bluetooth. It supports ISO 15118, local OCPP 1.6-J/2.0.1 and automatic phase switching. V2X readiness prepares the hardware for future services; it does not promise active V2G at delivery.',
    meter_note_title: 'Integrated energy meter with ±1% accuracy',
    meter_note_body: 'The manufacturer specifies ±1% accuracy, not a claim of MID certification or guaranteed tax refunds. V2X requires compatible vehicles, software, installation and supported services.',
    key_features: ['1.4–22 kW, subject to car and installation', 'Wi-Fi, Ethernet, 4G eSIM and Bluetooth BLE 4.2', 'V2X-ready with ISO 15118; V2G and Plug & Charge depend on setup', 'Local OCPP 1.6-J and 2.0.1', 'Automatic 3-to-1-phase switching', 'Optional NexBlue Zen dynamic load balancing', 'RFID access and electronic cable lock', 'Integrated meter with ±1% accuracy', 'IP54 / IK10 and UV-resistant enclosure', 'Five-year manufacturer warranty'],
    badge: 'Recommended',
  },
  easee_up: {
    tagline: "Denmark's best-seller · Smart",
    short_desc: "Denmark's best-selling charger right now",
    unique_point: 'Patented phase balancing – always charges optimally',
    badge: 'Coming soon',
  },
};

function translateSpecs(specs) {
  if (!Array.isArray(specs)) return specs;
  return specs.map((s) => ({
    label: SPEC_LABELS[s.label] || s.label,
    value: SPEC_VALUES[s.value] || s.value,
  }));
}

function translateTags(tags) {
  if (!Array.isArray(tags)) return tags;
  return tags.map((t) => SPEC_VALUES[t] || t);
}

/**
 * Return a shallow copy of a charger record with English-facing fields.
 * Falls back to the original Danish value when no translation exists.
 */
export function enCharger(c) {
  if (!c) return c;
  const o = BY_SKU[c.sku] || {};
  return {
    ...c,
    tagline: o.tagline || c.tagline,
    short_desc: o.short_desc || c.short_desc,
    unique_point: o.unique_point || c.unique_point,
    badge: c.sku === 'zaptec_go' && c.campaign_label ? 'Clearance sale' : o.badge || c.badge,
    campaign_label: c.sku === 'zaptec_go' && c.campaign_label === 'Spar 1.100 kr. · Parti på 10 stk. · Så længe lager haves' ? 'Save 1,100 DKK · Batch of 10 · While stocks last' : c.campaign_label,
    description: o.description || c.description,
    key_features: o.key_features || translateFeatures(c.key_features),
    meter_note_title: o.meter_note_title || c.meter_note_title,
    meter_note_body: o.meter_note_body || c.meter_note_body,
    specs: translateSpecs(c.specs),
    tags: translateTags(c.tags),
    features: translateFeatures(c.features),
  };
}

export default enCharger;